import React from 'react';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Breadcrumbs,
  Link
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import SEO from '../../common/SEO';

const FAQPage = () => {
  const faqs = [
    {
      category: 'Product & Licenses',
      questions: [
        { q: "How will I receive my product key?", a: "Your product key, official download links, and detailed installation guidelines will be delivered instantly to your registered email address right after checkout." },
        { q: "Are these product keys genuine?", a: "Yes, all our product keys are 100% genuine and authorized. They are sourced directly from Microsoft distribution channels and activate officially." },
        { q: "Are these keys for lifetime use?", a: "Yes, our Windows OS and Office licenses are lifetime keys. You pay once and receive lifetime activation on the designated computer." }
      ]
    },
    {
      category: 'Delivery & Setup',
      questions: [
        { q: "How long does delivery take?", a: "Delivery is instant and automated. The activation code is sent to your inbox within minutes of a successful purchase." },
        { q: "What if the key doesn't work?", a: "In the rare event that you experience activation issues, our technical support team is available 24/7. We offer a 100% activation guarantee or replacement." },
        { q: "Do you provide installation support?", a: "Yes, we offer complete step-by-step assistance. You can reach our support experts via WhatsApp for guided remote activation." }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        { q: "What is your return policy?", a: "Since product keys are digital goods and cannot be returned once revealed, we do not support general returns. However, we guarantee that the key will activate. If a key is defective, we provide an instant replacement." },
        { q: "How long do refunds take?", a: "If a refund is authorized due to activation issues that cannot be resolved, the funds will be credited to your payment method within 5-7 business days." }
      ]
    }
  ];

  // Build FAQ Schema for Google
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(group =>
      group.questions.map(q => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.a
        }
      }))
    )
  };

  return (
    <Container maxWidth="md" sx={{ py: 10 }}>
      <SEO
        title="Frequently Asked Questions (FAQ)"
        description="Got questions about genuine Microsoft product keys, delivery, or installation? Browse our comprehensive FAQ or contact support."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link underline="hover" color="inherit" href="/">Home</Link>
        <Typography color="text.primary">FAQ</Typography>
      </Breadcrumbs>

      <Typography variant="h2" sx={{ mb: 6, textAlign: 'center' }}>Frequently Asked Questions</Typography>

      {faqs.map((group, idx) => (
        <Box key={idx} sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'primary.main' }}>
            {group.category}
          </Typography>
          {group.questions.map((item, i) => (
            <Accordion key={i} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #EEE' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Container>
  );
};

export default FAQPage;
